document.addEventListener('DOMContentLoaded', () => {
  initHcCartToggle();
  document.addEventListener('theme:cart:refresh', initHcCartToggle);
  document.addEventListener('theme:product:added', initHcCartToggle);
  document.addEventListener('theme:cart-drawer:open', initHcCartToggle);
});

// Helper function to count how many items are currently upgraded
function countUpgradedItems(cartItems) {
  return cartItems.filter(item => 
    item.properties && item.properties._is_upsell_item
  ).length;
}

// Helper function to check if mystery gift should be kept
function shouldKeepMysteryGift(mysteryGiftProperties, currentCartItems) {
  if (!mysteryGiftProperties._mystery_gift_for) return false;
  
  // Keep mystery gift if there are any upgraded items
  return countUpgradedItems(currentCartItems) > 0;
}

function initHcCartToggle() {
  const toggles = document.querySelectorAll('.hc-cart-toggle__input');
  toggles.forEach(toggle => {
    if (!toggle.dataset.listenerAttached) {
      toggle.addEventListener('change', handleToggleChange);
      // Remove immediate visual change - will be handled after successful cart operation
      toggle.dataset.listenerAttached = 'true';
      
      // Initialize visual state on page load only
      initializeToggleVisualState(toggle);
    }
  });
}

function initializeToggleVisualState(toggle) {
  // Only set initial state, don't listen to changes
  const container = toggle.closest('.hc-cart-toggle__container');
  const textElement = container.querySelector('.hc-cart-toggle__text');
  
  // Get hidden text templates
  const textUncheckedElement = container.querySelector('.hc-cart-toggle__text-unchecked');
  const textCheckedElement = container.querySelector('.hc-cart-toggle__text-checked');
  
  const bgUnchecked = container.dataset.bgUnchecked;
  const bgChecked = container.dataset.bgChecked;
  const borderUnchecked = container.dataset.borderUnchecked;
  const borderChecked = container.dataset.borderChecked;
  
  if (toggle.checked) {
    // Set initial checked state
    if (textCheckedElement) {
      textElement.innerHTML = textCheckedElement.innerHTML;
    }
    container.style.setProperty('--toggle-bg-color', bgChecked);
    container.style.setProperty('--toggle-border-color', borderChecked);
  } else {
    // Set initial unchecked state
    if (textUncheckedElement) {
      textElement.innerHTML = textUncheckedElement.innerHTML;
    }
    container.style.setProperty('--toggle-bg-color', bgUnchecked);
    container.style.setProperty('--toggle-border-color', borderUnchecked);
  }
}

function updateToggleVisualState(toggle) {
  const container = toggle.closest('.hc-cart-toggle__container');
  const textElement = container.querySelector('.hc-cart-toggle__text');
  
  // Get hidden text templates
  const textUncheckedElement = container.querySelector('.hc-cart-toggle__text-unchecked');
  const textCheckedElement = container.querySelector('.hc-cart-toggle__text-checked');
  
  const bgUnchecked = container.dataset.bgUnchecked;
  const bgChecked = container.dataset.bgChecked;
  const borderUnchecked = container.dataset.borderUnchecked;
  const borderChecked = container.dataset.borderChecked;
  
  if (toggle.checked) {
    // Update text for checked state using innerHTML to support HTML
    if (textCheckedElement) {
      textElement.innerHTML = textCheckedElement.innerHTML;
    }
    // Update colors for checked state
    container.style.setProperty('--toggle-bg-color', bgChecked);
    container.style.setProperty('--toggle-border-color', borderChecked);
  } else {
    // Update text for unchecked state using innerHTML to support HTML
    if (textUncheckedElement) {
      textElement.innerHTML = textUncheckedElement.innerHTML;
    }
    // Update colors for unchecked state
    container.style.setProperty('--toggle-bg-color', bgUnchecked);
    container.style.setProperty('--toggle-border-color', borderUnchecked);
  }
}

function handleToggleVisualChange(event) {
  const toggle = event.currentTarget;
  const container = toggle.closest('.hc-cart-toggle__container');
  const textElement = container.querySelector('.hc-cart-toggle__text');
  
  // Get hidden text templates
  const textUncheckedElement = container.querySelector('.hc-cart-toggle__text-unchecked');
  const textCheckedElement = container.querySelector('.hc-cart-toggle__text-checked');
  
  const bgUnchecked = container.dataset.bgUnchecked;
  const bgChecked = container.dataset.bgChecked;
  const borderUnchecked = container.dataset.borderUnchecked;
  const borderChecked = container.dataset.borderChecked;
  
  if (toggle.checked) {
    // Update text for checked state using innerHTML to support HTML
    if (textCheckedElement) {
      textElement.innerHTML = textCheckedElement.innerHTML;
    }
    // Update colors for checked state
    container.style.setProperty('--toggle-bg-color', bgChecked);
    container.style.setProperty('--toggle-border-color', borderChecked);
  } else {
    // Update text for unchecked state using innerHTML to support HTML
    if (textUncheckedElement) {
      textElement.innerHTML = textUncheckedElement.innerHTML;
    }
    // Update colors for unchecked state
    container.style.setProperty('--toggle-bg-color', bgUnchecked);
    container.style.setProperty('--toggle-border-color', borderUnchecked);
  }
}

async function handleToggleChange(event) {
  const toggle = event.currentTarget;
  const lineItem = toggle.closest('.cart__item');
  
  // Store original state in case we need to revert
  const originalCheckedState = !toggle.checked; // Since this fires after the change

  // Activate loading state
  if (lineItem) {
    lineItem.classList.add('is-loading');
  }
  toggle.disabled = true;

  const { key, originalVariantId, upsellVariantId, quantity } = toggle.dataset;

  try {
    // 1. Get the current state of the cart to preserve order
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    const originalItems = cart.items;

    // 2. Find the index of the item being changed
    const itemIndex = originalItems.findIndex(item => item.key === key);
    if (itemIndex === -1) {
      throw new Error('Item not found in cart.');
    }

    // 3. Get mystery gift settings from theme
    const mysteryGiftEnabled = window.theme?.mysteryGift?.enabled || false;
    const mysteryGiftProducts = window.theme?.mysteryGift?.products || [];

    // 4. Create the new item object that will replace the old one
    let newItemData;
    if (toggle.checked) {
      // Data for the new UPGRADED item
      newItemData = {
        id: upsellVariantId,
        quantity: parseInt(quantity, 10),
        properties: {
          '_original_variant_id': originalVariantId,
          '_is_upsell_item': true,
        },
      };
    } else {
      // Data for the original DOWNGRADED item
      newItemData = {
        id: originalVariantId,
        quantity: parseInt(quantity, 10),
        properties: {},
      };
    }

    // 5. Create the final, ordered list of items for the new cart state.
    let finalItemsToAdd = originalItems.map((item, index) => {
      // Handle mystery gift items intelligently
      if (item.properties && item.properties._is_mystery_gift) {
        // Keep mystery gift only if there are still upgraded items
        if (shouldKeepMysteryGift(item.properties, originalItems)) {
          return {
            id: item.variant_id,
            quantity: item.quantity, // Will be updated below
            properties: item.properties || {},
          };
        }
        return null; // Remove mystery gift
      }
      
      if (index === itemIndex) {
        return newItemData; // Replace the old item with the new one at the same position
      }
      // Return existing items in a format suitable for the add.js API
      return {
        id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties || {},
      };
    }).filter(item => item !== null); // Remove null entries (removed mystery gifts)

    // 6. Calculate how many items will be upgraded after this change
    const upgradedCount = finalItemsToAdd.filter(item => 
      item.properties && item.properties._is_upsell_item
    ).length;

    // 7. Add or update mystery gift based on upgraded count
    if (mysteryGiftEnabled && mysteryGiftProducts.length > 0) {
      // Remove existing mystery gifts first
      finalItemsToAdd = finalItemsToAdd.filter(item => 
        !(item.properties && item.properties._is_mystery_gift)
      );

      if (upgradedCount > 0) {
        // Add all selected mystery gift products, each with quantity equal to upgraded count
        mysteryGiftProducts.forEach((product, index) => {
          const mysteryGiftItem = {
            id: product.variantId,
            quantity: upgradedCount, // Quantity equals number of upgraded items
            properties: {
              '_is_mystery_gift': true,
              '_mystery_gift_for': 'multiple',
              '_mystery_gift_index': index,
            },
          };
          finalItemsToAdd.push(mysteryGiftItem);
        });
        
        console.log('Mystery gifts added:', mysteryGiftProducts.length, 'products, each with quantity:', upgradedCount);
      } else {
        console.log('Mystery gifts removed - no upgraded items');
      }
    }

    console.log('Final items to add:', finalItemsToAdd);

    // 7. Clear the cart completely
    await fetch('/cart/clear.js', { method: 'POST' });

    // 8. Re-add all items in REVERSE ORDER to counteract the API's prepending behavior.
    for (let i = finalItemsToAdd.length - 1; i >= 0; i--) {
      const item = finalItemsToAdd[i];
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item),
      });
    }

    // Only update visual state after successful cart operation
    updateToggleVisualState(toggle);

  } catch (error) {
    console.error('Error rebuilding cart to preserve order:', error);
    
    // Revert toggle to original state on error
    toggle.checked = originalCheckedState;
    
    // Re-enable toggle in case of failure
    toggle.disabled = false;
  } finally {
    // Deactivate loading state before refreshing the cart
    if (lineItem) {
      lineItem.classList.remove('is-loading');
    }
    // 9. Dispatch the theme's event to refresh the entire cart UI
    document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
  }
}